import React, { FC, useMemo } from 'react';
import { SiteAreaAccessTypeQuestion } from 'core/accessRequestForm/types';
import TextInput from 'components/Elements/TextInput';
import Checkbox from 'components/Elements/Checkbox';
import { RadioCheckedIcon, RadioUncheckedIcon, WarningIcon } from 'components/Elements/CustomSVGIcon';
import Tooltip from 'components/Elements/Tootltip';
import { MAX_LONG_TEXT_LENGTH } from 'core/constants';
import './dynamicQuestions.scss';

interface DynamicQuestionsProps {
    questions: SiteAreaAccessTypeQuestion[];
    answers: Record<number, string>;
    onChange: (questionId: number, value: string) => void;
}

const DynamicQuestions: FC<DynamicQuestionsProps> = ({
    questions,
    answers,
    onChange,
}) => {
    const sortedQuestions = useMemo(
        () => [...questions].sort((a, b) => a.sortOrder - b.sortOrder),
        [questions],
    );

    if (questions.length === 0) {
        return null;
    }

    const renderLabel = (question: SiteAreaAccessTypeQuestion) => (
        <div className="question-label-wrapper">
            <h5 className="heading">
                {question.label}
                {question.isRequired && <span className="required-indicator"> *</span>}
            </h5>
            {question.hasInfoBox && question.infoBoxText && (
                <Tooltip title={question.infoBoxText}>
                    <div className="warning-icon-wrapper">
                        <WarningIcon />
                    </div>
                </Tooltip>
            )}
        </div>
    );

    const renderField = (question: SiteAreaAccessTypeQuestion) => {
        const value = answers[question.id] || '';

        switch (question.type) {
        case 'text':
            return (
                <div className="text-input-wrapper">
                    <TextInput
                        value={value}
                        onChange={(e) => onChange(question.id, e.target.value)}
                        placeholder={`Enter ${question.label.toLowerCase()}`} />
                </div>
            );

        case 'textarea':
            return (
                <textarea
                    className="question-textarea"
                    value={value}
                    maxLength={MAX_LONG_TEXT_LENGTH}
                    onChange={(e) => onChange(question.id, e.target.value)}
                    placeholder={`Enter ${question.label.toLowerCase()}`} />
            );

        case 'boolean':
            return (
                <div className="boolean-option">
                    <Checkbox
                        checked={value === 'true'}
                        onClick={() => onChange(question.id, value === 'true' ? 'false' : 'true')} />
                </div>
            );

        case 'select': {
            const selectOptions = question.options || [];

            if (question.allowMultiple) {
                let selectedValues: string[] = [];
                try {
                    selectedValues = value ? JSON.parse(value) : [];
                } catch {
                    selectedValues = value ? [value] : [];
                }

                const handleCheckboxClick = (opt: string) => {
                    let updated: string[];
                    if (selectedValues.includes(opt)) {
                        updated = selectedValues.filter((v) => v !== opt);
                    } else {
                        updated = [...selectedValues, opt];
                    }
                    onChange(question.id, JSON.stringify(updated));
                };

                return (
                    <div className="checkbox-options">
                        {selectOptions.map((opt) => (
                            <div key={opt} className="checkbox-option">
                                <Checkbox
                                    checked={selectedValues.includes(opt)}
                                    onClick={() => handleCheckboxClick(opt)} />
                                <span className="option-label">{opt}</span>
                            </div>
                        ))}
                    </div>
                );
            }

            return (
                <div className="radio-options">
                    {selectOptions.map((opt) => (
                        <div
                            key={opt}
                            className="radio-option"
                            onClick={() => onChange(question.id, opt)}>
                            <div className="radio-icon">{value === opt ? <RadioCheckedIcon /> : <RadioUncheckedIcon />}</div>
                            <span className="option-label">{opt}</span>
                        </div>
                    ))}
                </div>
            );
        }

        default:
            return null;
        }
    };

    return (
        <div className="dynamic-questions-container">
            {sortedQuestions.map((question) => (
                <div key={question.id} className="question-wrapper">
                    {renderLabel(question)}
                    {renderField(question)}
                </div>
            ))}
        </div>
    );
};

export default DynamicQuestions;
